/**
 * Helpery do czytania z repliki i kolejkowania mutacji.
 */

import { loadFromReplica, saveToReplica } from "./replica";
import { enqueue } from "./outbox";
import { offlineFetch } from "./offlineFetch";
import { stripSecretsFromBody } from "./sanitize";
import { rememberOutboxPassword } from "./ephemeralPasswords";
import type { ReplicaKey, OutboxMethod } from "./types";

export async function loadWithReplica<T>(
  key: ReplicaKey,
  fetcher: () => Promise<{ data?: unknown; error?: unknown }>,
): Promise<{ data: T | null; fromReplica: boolean; error?: unknown }> {
  if (!navigator.onLine) {
    const cached = await loadFromReplica<T>(key);
    if (cached) {
      return { data: cached, fromReplica: true };
    }
    return {
      data: null,
      fromReplica: true,
      error: "Offline and no cached data",
    };
  }

  try {
    const response = await fetcher();

    if (response.error) {
      const cached = await loadFromReplica<T>(key);
      if (cached) {
        return { data: cached, fromReplica: true };
      }
      return { data: null, fromReplica: false, error: response.error };
    }

    if (response.data !== undefined) {
      await saveToReplica(key, response.data);
    }

    return { data: (response.data as T) ?? null, fromReplica: false };
  } catch (error) {
    const cached = await loadFromReplica<T>(key);
    if (cached) {
      return { data: cached, fromReplica: true };
    }
    return { data: null, fromReplica: false, error };
  }
}

export type MutateOrQueueResult =
  | { success: true; online: true; data?: unknown }
  | {
      success: true;
      online: false;
      queueId: number;
      clientId: string;
      needsPassword: boolean;
    }
  | { success: false; error: unknown };

async function enqueueSanitized(
  method: OutboxMethod,
  path: string,
  body: Record<string, unknown>,
  clientId?: string,
): Promise<{ queueId: number; clientId: string; needsPassword: boolean }> {
  const finalClientId = clientId || crypto.randomUUID();
  const { sanitized, password } = stripSecretsFromBody({ ...body, client_id: finalClientId });
  const needsPassword = Boolean(password);
  if (password) {
    rememberOutboxPassword(finalClientId, password);
  }
  const queueId = await enqueue(method, path, sanitized, finalClientId, "pending");
  return { queueId, clientId: finalClientId, needsPassword };
}

/**
 * Online: ten sam CSRF/401 co reszta API.
 * Offline / network error: kolejka bez sekretów w IndexedDB.
 * Jeśli mutacja miała hasło i karta się zamknie przed drainem — needs_password.
 */
/**
 * Zawsze próbuje fetch do serwera. Decyzja kolejka/error na podstawie typu błędu:
 * - Sukces → return data
 * - TypeError/NetworkError → kolejka (brak sieci)
 * - 5xx/timeout → kolejka (serwer padł, spróbujemy później)
 - 4xx → error (walidacja, nie kolejkować)
 * - 401 → error (offlineFetch już przekierował)
 */
export async function mutateOrQueue(
  method: OutboxMethod,
  path: string,
  body: Record<string, unknown>,
  clientId?: string,
): Promise<MutateOrQueueResult> {
  try {
    const response = await offlineFetch(method, path, body);
    
    if (response.ok) {
      return { success: true, online: true, data: response.data };
    }

    // 5xx → kolejka (serwer padł, sync po powrocie)
    if (response.status >= 500) {
      try {
        const queued = await enqueueSanitized(method, path, body, clientId);
        return { success: true, online: false, ...queued };
      } catch (queueError) {
        return { success: false, error: String(queueError) };
      }
    }

    // 4xx (poza 401 który już przekierował) → twardy błąd
    return { success: false, error: response.error };
  } catch (error) {
    // TypeError/NetworkError → brak sieci, kolejka
    if (error instanceof TypeError || (error && (error as Error).name === "NetworkError")) {
      try {
        const queued = await enqueueSanitized(method, path, body, clientId);
        return { success: true, online: false, ...queued };
      } catch (queueError) {
        return { success: false, error: String(queueError) };
      }
    }
    
    // Inny błąd (timeout itp.) → kolejka
    try {
      const queued = await enqueueSanitized(method, path, body, clientId);
      return { success: true, online: false, ...queued };
    } catch (queueError) {
      return { success: false, error: String(queueError) };
    }
  }
}

export function offlineQueuedInfo(needsPassword: boolean): string {
  if (needsPassword) {
    return "Zapisano na tym urządzeniu. Jeśli zamkniesz kartę przed synchronizacją, potwierdź hasło w statusie połączenia.";
  }
  return "Zapisano na tym urządzeniu. Synchronizacja nastąpi automatycznie po połączeniu z siecią.";
}
